import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Enable detection of auth parameters in URL
    storage: localStorage,
    storageKey: 'sb-auth-token',
    flowType: 'pkce' // Use PKCE flow for better security
  },
  global: {
    headers: {
      'X-Client-Info': 'agrotrack'
    }
  },
  // Add retry configuration
  fetch: (url, options) => {
    const fetchWithRetry = async (retriesLeft = 3, delay = 1000) => {
      try {
        return await fetch(url, options);
      } catch (error) {
        if (retriesLeft <= 0) throw error;
        
        // Wait for the specified delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry with one less retry remaining and increased delay
        return fetchWithRetry(retriesLeft - 1, delay * 1.5);
      }
    };
    
    return fetchWithRetry();
  }
});

// Enhanced error handling utility
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  if (error?.status === 503) {
    return 'Service temporarily unavailable. Please try again in a few moments.';
  }
  
  if (error?.status >= 500) {
    return 'Server error occurred. Please try again later.';
  }
  
  if (error?.status === 401) {
    return 'Authentication failed. Please check your credentials.';
  }
  
  if (error?.status === 403) {
    return 'Access denied. You do not have permission to perform this action.';
  }
  
  if (error?.status === 400) {
    return 'Invalid request. Please check your input and try again.';
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

// Connection health check utility
export const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.from('users').select('count').limit(1);
    return !error;
  } catch (error) {
    console.error('Supabase connection check failed:', error);
    return false;
  }
};